/**
 * Service Provider App Settings
 *
 * Service-provider connections in connections.json must reference secret /
 * config-bound parameters via `@appsetting('NAME')` rather than embedding the
 * raw value (the Standard runtime's `ServiceProviderEngine` resolves these
 * references at execution time; an inline string is not accepted).
 *
 * This module:
 *   - Extracts the values that must be moved to app settings from a built
 *     parameter-value map, keyed by the connector manifest's
 *     `parameterSource === 'AppConfiguration'` marker.
 *   - Produces the replaced parameter-value map and the app-setting payload.
 *   - Provides a resolver that re-inflates `@appsetting('NAME')` references
 *     against a settings map for display/editing of existing connections.
 *
 * Naming convention mirrors LogicAppsUX, with an Otto-specific prefix:
 *   `connectionreference_${escape(connectionKey)}_${escape(parameterKey)}`
 *   where `escape` replaces `-` with `_1` so the result is a valid env-var key.
 *   The `connectionreference_` prefix marks the setting as a connection secret
 *   (see `CONNECTION_REFERENCE_SETTING_PREFIX`).
 */

import type { ConnectionParameterSet } from '@/types/connectionParameters';

// =============================================================================
// CONSTANTS
// =============================================================================

const APP_SETTING_REFERENCE_REGEX = /^@appsetting\(\s*'([^']+)'\s*\)$/;

/**
 * Prefix applied to every connection-derived app-setting name. It marks a
 * setting as owned by a connection reference so downstream consumers can tell
 * connection secrets apart from arbitrary user settings. In particular, the
 * publish-time draft→production settings merge lets a DRAFT value override a
 * conflicting PRODUCTION value ONLY for settings carrying this prefix (see
 * `mergeDraftIntoProd` in `@/services/environmentSettingsApi`).
 */
export const CONNECTION_REFERENCE_SETTING_PREFIX = 'connectionreference_';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Sanitize a segment for use in an app-setting name. Mirrors
 * LogicAppsUX's `escapeSpecialChars`: replace `-` with `_1`.
 */
export function escapeAppSettingChars(value: string): string {
  return value.replace(/-/g, '_1');
}

/**
 * Build the canonical app-setting name for a connection parameter. Every name
 * is prefixed with `connectionreference_` so the setting is identifiable as a
 * connection secret (see `CONNECTION_REFERENCE_SETTING_PREFIX`).
 */
export function formatAppSettingName(connectionKey: string, parameterKey: string): string {
  return `${CONNECTION_REFERENCE_SETTING_PREFIX}${escapeAppSettingChars(connectionKey)}_${escapeAppSettingChars(parameterKey)}`;
}

/** Wrap an app-setting name in the `@appsetting('NAME')` reference syntax. */
export function formatAppSettingReference(name: string): string {
  return `@appsetting('${name}')`;
}

/** True when `value` is a string of the form `@appsetting('NAME')`. */
export function isAppSettingReference(value: unknown): value is string {
  return typeof value === 'string' && APP_SETTING_REFERENCE_REGEX.test(value);
}

/**
 * Extract the app-setting name from `@appsetting('NAME')`, or `null` if the
 * input is not an app-setting reference.
 */
export function getAppSettingNameFromReference(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = APP_SETTING_REFERENCE_REGEX.exec(value);
  return match?.[1] ?? null;
}

// =============================================================================
// EXTRACT (write path)
// =============================================================================

export interface ServiceProviderAppSettingsResult {
  /**
   * Parameter values with `AppConfiguration`-sourced leaves replaced by
   * `@appsetting('NAME')` references. Suitable for writing to
   * `serviceProviderConnections[*].parameterValues` in connections.json.
   */
  parameterValues: Record<string, unknown>;
  /**
   * Map of app-setting name -> raw value to upsert into environment settings.
   * Non-string values are JSON-stringified to match the LogicAppsUX behavior.
   */
  appSettings: Record<string, string>;
}

/**
 * Walk `parameterValues` against the connector's parameter-set definition.
 * For every leaf whose parameter declares `parameterSource: 'AppConfiguration'`,
 * move the value to app settings and replace the leaf with an `@appsetting()`
 * reference. Other leaves are passed through unchanged.
 *
 * `parameterValues` may contain nested objects produced by
 * `prepareParameterValuesForApi` (e.g. fields nested under `authProvider` via
 * `propertyPath`). The walk follows the parameter's `propertyPath` to locate
 * the leaf in the nested structure.
 */
export function extractServiceProviderAppSettings(
  connectionKey: string,
  parameterValues: Record<string, unknown>,
  parameterSet: ConnectionParameterSet | undefined,
): ServiceProviderAppSettingsResult {
  const next = deepClonePlain(parameterValues);
  const appSettings: Record<string, string> = {};

  if (!parameterSet?.parameters) {
    return { parameterValues: next, appSettings };
  }

  for (const [parameterKey, paramDef] of Object.entries(parameterSet.parameters)) {
    if (paramDef?.parameterSource !== 'AppConfiguration') continue;

    const path = [...(paramDef.uiDefinition?.constraints?.propertyPath ?? []), parameterKey];
    const value = getAtPath(next, path);
    if (value === undefined || value === null || value === '') continue;
    if (isAppSettingReference(value)) continue;

    const settingName = formatAppSettingName(connectionKey, parameterKey);
    appSettings[settingName] = typeof value === 'string' ? value : JSON.stringify(value);
    setAtPath(next, path, formatAppSettingReference(settingName));
  }

  return { parameterValues: next, appSettings };
}

// =============================================================================
// RESOLVE (read path)
// =============================================================================

/**
 * Replace every `@appsetting('NAME')` leaf inside `parameterValues` with the
 * resolved value from `appSettings`. Leaves that reference an unknown setting
 * are left as-is so the caller can surface a missing-setting warning.
 */
export function resolveAppSettingReferences(
  parameterValues: Record<string, unknown>,
  appSettings: Record<string, string | null | undefined>,
): Record<string, unknown> {
  return mapLeaves(parameterValues, (leaf) => {
    const name = getAppSettingNameFromReference(leaf);
    if (name === null) return leaf;
    const resolved = appSettings[name];
    return resolved == null ? leaf : resolved;
  });
}

/**
 * True when any leaf inside `parameterValues` is an `@appsetting('NAME')`
 * reference. Used to short-circuit env-settings fetches when there's nothing
 * to resolve.
 */
export function hasAppSettingReferences(parameterValues: Record<string, unknown>): boolean {
  for (const v of Object.values(parameterValues)) {
    if (isPlainObject(v)) {
      if (hasAppSettingReferences(v)) return true;
    } else if (isAppSettingReference(v)) {
      return true;
    }
  }
  return false;
}

// =============================================================================
// INTERNAL
// =============================================================================

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepClonePlain(value: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = isPlainObject(v) ? deepClonePlain(v) : v;
  }
  return out;
}

function getAtPath(obj: Record<string, unknown>, path: string[]): unknown {
  let cur: unknown = obj;
  for (const seg of path) {
    if (!isPlainObject(cur)) return undefined;
    cur = cur[seg];
  }
  return cur;
}

function setAtPath(obj: Record<string, unknown>, path: string[], value: unknown): void {
  if (path.length === 0) return;
  let cur = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const seg = path[i]!;
    const existing = cur[seg];
    if (!isPlainObject(existing)) {
      const next: Record<string, unknown> = {};
      cur[seg] = next;
      cur = next;
    } else {
      cur = existing;
    }
  }
  cur[path[path.length - 1]!] = value;
}

function mapLeaves(
  value: Record<string, unknown>,
  fn: (leaf: unknown) => unknown,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = isPlainObject(v) ? mapLeaves(v, fn) : fn(v);
  }
  return out;
}
